import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MitarbeiterService } from '../../../services/mitarbeiter.service';
import { Kontrolltermin } from '../../../services/kontrolltermin.service';
import { AuthQuery } from '@app/auth/auth.query';

export interface ChangeTechnikerDialogData {
  kontrolltermin: Kontrolltermin;
  currentTechnikerId: string | number;
}

@Component({
  selector: 'app-change-techniker-dialog',
  templateUrl: './change-techniker-dialog.component.html',
  styleUrls: ['./change-techniker-dialog.component.scss']
})
export class ChangeTechnikerDialogComponent implements OnInit {
  isLoading = false;
  isSaving = false;
  
  // Available techniker options
  technikerOptions: any[] = [];
  
  // Form fields
  selectedTechnikerId: string | number = '';
  pruefdatum: string = ''; // ISO format: YYYY-MM-DD (for browser datepicker)
  sollZeit: number | null = null;
  istZeit: number | null = null;
  
  // Permission flags
  canEditPruefdatum: boolean = false;
  canEditZeiten: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<ChangeTechnikerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeTechnikerDialogData,
    private mitarbeiterService: MitarbeiterService,
    private snackBar: MatSnackBar,
    private authQuery: AuthQuery
  ) {}

  ngOnInit(): void {
    this.checkPermissions();
    this.loadTechnikers();
    this.populateFields();
  }

  private checkPermissions(): void {
    const role = this.authQuery?.role;
    const roleNum = typeof role === 'string' ? parseInt(role, 10) : role;
    
    // Prüfdatum: SuperUser, CompAdmin, Büro
    this.canEditPruefdatum = roleNum === 1 || roleNum === 2 || roleNum === 11;
    
    // Zeiten: Can be edited in bearbeitung status (handled by status check)
    this.canEditZeiten = true; // Will be filtered by status in the template
  }

  private populateFields(): void {
    // Populate prüfdatum in ISO format (YYYY-MM-DD) for browser datepicker
    if (this.data.kontrolltermin?.datum_durchfuerung) {
      const date = new Date(this.data.kontrolltermin.datum_durchfuerung);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        this.pruefdatum = `${year}-${month}-${day}`;
      }
    }
    
    // Populate times
    this.sollZeit = this.data.kontrolltermin?.soll_zeit || null;
    this.istZeit = this.data.kontrolltermin?.ist_zeit || null;
  }

  private loadTechnikers(): void {
    this.isLoading = true;
    
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.technikerOptions = response.data || [];
        this.isLoading = false;
        this.populateForm();
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoading = false;
        this.populateForm();
      }
    });
  }

  private populateForm(): void {
    const currentTechnikerId = this.data.currentTechnikerId || this.data.kontrolltermin.techniker_id;
    
    // Handle data type mismatch: kontrolltermin has string ID, techniker options have numeric ID
    if (currentTechnikerId && this.technikerOptions.length > 0) {
      const numericId = typeof currentTechnikerId === 'string' ? parseInt(currentTechnikerId, 10) : currentTechnikerId;
      
      const foundTechniker = this.technikerOptions.find(t => 
        t.id === numericId || 
        t.id === currentTechnikerId ||
        t.id === currentTechnikerId.toString()
      );
      
      if (foundTechniker) {
        this.selectedTechnikerId = foundTechniker.id;
      } else {
        this.selectedTechnikerId = numericId;
      }
    } else {
      this.selectedTechnikerId = currentTechnikerId || '';
    }
  }

  getCurrentTechnikerName(): string {
    if (!this.data.kontrolltermin?.techniker) return '-';
    
    const techniker: any = this.data.kontrolltermin.techniker;
    
    if (techniker.full_name) return techniker.full_name;
    if (techniker.display_name) return techniker.display_name;
    if (techniker.name) return techniker.name;
    
    // Fallback to vorname + nachname if available
    const vorname = techniker.vorname || techniker.first_name || '';
    const nachname = techniker.nachname || techniker.last_name || '';
    const name = (vorname + ' ' + nachname).trim();
    
    return name || '-';
  }

  onSave(): void {
    console.log('ChangeTechnikerDialog: onSave() called');
    console.log('ChangeTechnikerDialog: selectedTechnikerId:', this.selectedTechnikerId);
    console.log('ChangeTechnikerDialog: pruefdatum:', this.pruefdatum);
    console.log('ChangeTechnikerDialog: sollZeit:', this.sollZeit);
    console.log('ChangeTechnikerDialog: istZeit:', this.istZeit);
    
    if (!this.selectedTechnikerId) {
      console.log('ChangeTechnikerDialog: No techniker selected, showing error');
      this.snackBar.open('Bitte wählen Sie einen Techniker aus', 'Schließen', { duration: 3000 });
      return;
    }
    
    console.log('ChangeTechnikerDialog: Setting isSaving = true');
    this.isSaving = true;
    
    const result: any = { 
      event: 'save', 
      techniker_id: this.selectedTechnikerId 
    };
    
    // Add prüfdatum if provided (already in ISO format YYYY-MM-DD from browser datepicker)
    if (this.pruefdatum && this.canEditPruefdatum) {
      result.datum_durchfuerung = this.pruefdatum;
    }
    
    // Add times if provided
    if (this.sollZeit !== null && this.sollZeit !== undefined) {
      result.soll_zeit = this.sollZeit;
    }
    if (this.istZeit !== null && this.istZeit !== undefined) {
      result.ist_zeit = this.istZeit;
    }
    
    console.log('ChangeTechnikerDialog: Closing dialog with result:', result);
    
    // Close dialog and return all changed fields
    this.dialogRef.close(result);
    console.log('ChangeTechnikerDialog: Dialog close() called');
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}



